/**
 * Real-backend tenant data provider (Phase 3 cut-over).
 *
 * Fetches `/api/tenants/{tenant_id}/bundle` once the user is
 * authenticated and exposes the SAME shape `useTenantData()` already
 * speaks — every consumer page works unchanged.
 *
 * State:
 *   - bundle:    the JSON the server returned (rich for demo tenants,
 *                empty for customer-mode tenants).
 *   - overlay:   in-memory session overlay for opportunities /
 *                deployment-workflow decisions / audit entries the user
 *                creates this session. Mirrors the mock provider's
 *                overlay semantics. Phase 4 will write these to Mongo
 *                via mutation endpoints; for today they stay client-side.
 *   - loading + error states so consumer pages can render a skeleton.
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { TenantModeContext } from "@/contexts/TenantModeContext";
import { AuthContext } from "@/contexts/AuthContext";
import { api, ApiError } from "@/lib/api";
import { buildOpportunitySubmission } from "@/lib/opportunitySubmission";
import { applyDecision } from "@/lib/deploymentWorkflow";
import { logger } from "@/lib/logger";

const EMPTY_OVERLAY = {
  opportunities: [],
  requests: [],
  requestOverrides: {},
  auditEntries: [],
};

function mergeOverlay(base, overlay) {
  const overrideCount = Object.keys(overlay.requestOverrides).length;
  const isEmpty =
    !overlay.opportunities.length &&
    !overlay.requests.length &&
    !overlay.auditEntries.length &&
    overrideCount === 0;
  if (isEmpty) return base;
  const applyOverride = (r) => overlay.requestOverrides[r.id] || r;
  return {
    ...base,
    opportunities: [...overlay.opportunities, ...(base.opportunities || [])],
    requests: [...overlay.requests.map(applyOverride), ...(base.requests || []).map(applyOverride)],
    auditEntries: [...overlay.auditEntries, ...(base.auditEntries || [])],
  };
}

export function ApiTenantProvider({ children }) {
  const auth = useContext(AuthContext);
  const tenant = auth?.tenant;
  const isAuthenticated = auth?.isAuthenticated;
  const hydrated = auth?.hydrated;
  const tenantId = tenant?.id || null;

  const [rawBundle, setRawBundle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [overlay, setOverlay] = useState(EMPTY_OVERLAY);
  // Tracks the tenantId we most recently kicked a fetch for. Lets us
  // skip StrictMode-driven double mounts + spurious re-renders that
  // don't actually change the fetch key.
  const lastRequestedTenantId = useRef(null);

  const applyFetchResult = useCallback((result) => {
    if (result.kind === "reset") {
      setRawBundle(null);
      return;
    }
    if (result.kind === "start") {
      setLoading(true);
      setError(null);
      setOverlay(EMPTY_OVERLAY);
      return;
    }
    if (result.kind === "success") {
      setRawBundle(result.data);
      setLoading(false);
      return;
    }
    if (result.kind === "failure") {
      logger.warn("[tenant] bundle fetch failed", result.error);
      setError(result.error instanceof ApiError ? result.error.message : "Network error");
      setRawBundle(null);
      setLoading(false);
    }
  }, []);

  // Fetch the bundle whenever the authenticated tenant changes.
  useEffect(() => {
    let cancelled = false;
    if (!hydrated || !isAuthenticated || !tenantId) {
      if (lastRequestedTenantId.current !== null) {
        lastRequestedTenantId.current = null;
        Promise.resolve().then(() => {
          if (!cancelled) applyFetchResult({ kind: "reset" });
        });
      }
      return () => { cancelled = true; };
    }
    // Dedupe: if we've already started a fetch for this tenantId in
    // this provider instance, skip the duplicate (covers StrictMode +
    // benign auth re-renders).
    if (lastRequestedTenantId.current === tenantId) {
      return () => { cancelled = true; };
    }
    lastRequestedTenantId.current = tenantId;
    Promise.resolve()
      .then(() => { if (!cancelled) applyFetchResult({ kind: "start" }); })
      .then(() => api.get(`/api/tenants/${tenantId}/bundle`))
      .then((data) => { if (!cancelled) applyFetchResult({ kind: "success", data }); })
      .catch((e) => {
        if (!cancelled) {
          // Allow a retry for the same id if the request failed.
          lastRequestedTenantId.current = null;
          applyFetchResult({ kind: "failure", error: e });
        }
      });
    return () => { cancelled = true; };
  }, [tenantId, isAuthenticated, hydrated, applyFetchResult]);

  const submitOpportunityRequest = useCallback(async (payload) => {
    // Phase 5: real backend write via POST /api/opportunities. The
    // server returns the canonical opportunity + linked deployment
    // request + hash-chained audit entry; we mirror them into the
    // session overlay so consumer pages re-render without a refetch.
    const idem = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const result = await api.post("/api/opportunities", payload, {
      "Idempotency-Key": idem,
    });
    const { opportunity, request, audit } = result;
    setOverlay((prev) => ({
      ...prev,
      opportunities: [opportunity, ...prev.opportunities],
      requests: [request, ...prev.requests],
      auditEntries: [audit, ...prev.auditEntries],
    }));
    return { opportunity, request, audit };
  }, []);

  const advanceDeploymentStage = useCallback(async (currentRequest, decision, _actor) => {
    // Phase 5: real backend write via POST
    // /api/deployment-requests/{id}/decision. Server runs the same
    // state-machine logic ports of `lib/deploymentWorkflow.js` lived
    // in, plus stage-specific RBAC + hash-chained audit.
    const idem = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const result = await api.post(
      `/api/deployment-requests/${currentRequest.id}/decision`,
      { decision: decision === "deploy" ? "deploy" : decision },
      { "Idempotency-Key": idem },
    );
    const { request: updated, audit, spawned_agent: spawnedAgent } = result;
    setOverlay((prev) => ({
      ...prev,
      // Replace the pending entry in-place if this DR was created during
      // the session (promote / submit flows); also write to overrides so
      // server-sourced rows pick up the new stage. Both consumers
      // (`requests` and `requestOverrides`) need the fresh state.
      requests: prev.requests.map((r) => (r.id === updated.id ? updated : r)),
      requestOverrides: { ...prev.requestOverrides, [updated.id]: updated },
      auditEntries: audit ? [audit, ...prev.auditEntries] : prev.auditEntries,
    }));
    // When an agent is born, refetch the bundle so every page picks up
    // the new propagation (kpis, top burners, frameworks).
    if (spawnedAgent) {
      try {
        const fresh = await api.get(`/api/tenants/${tenantId}/bundle`);
        setRawBundle(fresh);
      } catch (e) {
        logger.warn("[tenant] bundle refetch after spawn failed", e);
      }
    }
    return { request: updated, audit, spawnedAgent };
  }, [tenantId]);

  const promoteOpportunity = useCallback(async (opportunityId) => {
    const idem = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const result = await api.post(
      `/api/opportunities/${opportunityId}/promote`,
      {},
      { "Idempotency-Key": idem },
    );
    const { opportunity, request, audit } = result;
    setOverlay((prev) => ({
      ...prev,
      requests: [request, ...prev.requests.filter((r) => r.id !== request.id)],
      requestOverrides: { ...prev.requestOverrides, [opportunity.id]: opportunity },
      auditEntries: audit ? [audit, ...prev.auditEntries] : prev.auditEntries,
    }));
    // Refetch so the opportunity status flips to in-flight on every page.
    try {
      const fresh = await api.get(`/api/tenants/${tenantId}/bundle`);
      setRawBundle(fresh);
    } catch (e) {
      logger.warn("[tenant] bundle refetch after promote failed", e);
    }
    return { opportunity, request, audit };
  }, [tenantId]);

  const resetDemo = useCallback(async () => {
    await api.post(`/api/demo/reset`, {});
    setOverlay(EMPTY_OVERLAY);
    const fresh = await api.get(`/api/tenants/${tenantId}/bundle`);
    setRawBundle(fresh);
  }, [tenantId]);

  const replayDemo = useCallback(async ({ heroOpportunityId, stepDelayMs = 600, onStep } = {}) => {
    // Demo orchestrator: reset → promote → 6 sequential approvals.
    // Emits step updates via the optional `onStep(stepIdx, label)` so
    // the UI can highlight progress. Returns the spawned agent.
    const tenantPrefix = tenantId.startsWith("acme") ? "BANK" : "INS";
    const heroId = heroOpportunityId || (tenantPrefix === "BANK" ? "OPP-BANK-2020" : "OPP-INS-9020");
    const emit = (idx, label) => onStep && onStep(idx, label);

    emit(0, "Resetting demo state…");
    await api.post(`/api/demo/reset`, {});

    emit(1, "Promoting opportunity…");
    const promoteResult = await api.post(
      `/api/opportunities/${heroId}/promote`,
      {},
      { "Idempotency-Key": (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}` },
    );
    const drId = promoteResult.request.id;

    let spawned = null;
    const stageLabels = [
      "Risk assessment approved",
      "IT Security review approved",
      "Legal sign-off approved",
      "Policy binding complete",
      "Final approval",
      "Deploying to production…",
    ];
    for (let i = 0; i < 6; i += 1) {
      emit(2 + i, stageLabels[i]);
      // Small delay between approvals so the user can see the toasts.
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, stepDelayMs));
      // eslint-disable-next-line no-await-in-loop
      const stepResult = await api.post(
        `/api/deployment-requests/${drId}/decision`,
        { decision: "approve" },
        { "Idempotency-Key": (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${i}` },
      );
      if (stepResult?.spawned_agent) {
        spawned = stepResult.spawned_agent;
      }
    }

    emit(8, "Agent is LIVE");
    // Refetch bundle so every page reflects propagation.
    const fresh = await api.get(`/api/tenants/${tenantId}/bundle`);
    setRawBundle(fresh);
    setOverlay(EMPTY_OVERLAY);
    return { agent: spawned, drId, opportunityId: heroId };
  }, [tenantId]);

  const recordAuditChainVerify = useCallback((auditEntry) => {
    if (!auditEntry) return;
    setOverlay((prev) => ({
      ...prev,
      auditEntries: [auditEntry, ...prev.auditEntries],
    }));
  }, []);

  const merged = useMemo(() => {
    if (!rawBundle) return null;
    return mergeOverlay(rawBundle, overlay);
  }, [rawBundle, overlay]);

  // Mode + setMode are no-ops in API mode (Phase 4 will let admins flip
  // a tenant between modes server-side). We expose them for shape parity
  // with the mock provider so the existing `EnvSwitcher` keeps compiling.
  const setMode = useCallback(() => {
    logger.info("[tenant] setMode is a no-op in API mode");
  }, []);
  const setDemoTenantId = useCallback(() => {
    logger.info("[tenant] setDemoTenantId is a no-op in API mode (use sign-in)");
  }, []);

  const value = useMemo(() => {
    // Fallback tenant from /api/me so the shell can render its breadcrumb
    // (and dependent chrome) during the brief window before the bundle
    // request resolves.
    const fallbackTenant = tenant || null;
    const mode = (rawBundle?.tenant?.mode) || fallbackTenant?.mode || "customer";
    const baseEmpty = {
      tenant: rawBundle?.tenant || fallbackTenant,
      vendors: [], vendorDetails: {}, vendorCatalogue: [],
      agents: [], opportunities: [], policies: [], incidents: [],
      hilQueue: [], driftSignals: [], requests: [], frameworks: [],
      auditEntries: [], reports: [], reportPacks: [], scheduledReports: [],
      costTrend: [], costOpportunities: [], costByDepartment: [],
      benchmark: null, users: [],
      departmentRollups: [], vendorRollups: [], vendorBurnRollup: [],
      topBurners: [], trend12m: [],
      kpis: { governedAgents: 0, totalAgents: 0, monthlySpendAud: 0, monthlyValueAud: 0, roiRatio: 0, openHighIncidents: 0, violations: 0, ungoverned: 0, pendingApprovals: 0, avgComplianceCoverage: 0 },
      isEmpty: true,
    };
    return {
      mode,
      setMode,
      demoTenantId: tenantId,
      setDemoTenantId,
      availableDemoTenants: [],     // populated later when tenant-list endpoint ships
      isDemo: mode === "demo",
      isCustomer: mode !== "demo",
      submitOpportunityRequest,
      advanceDeploymentStage,
      promoteOpportunity,
      resetDemo,
      replayDemo,
      recordAuditChainVerify,
      // Expose the overlay slice so migrated pages (Phase 3b) can
      // merge optimistic items on top of REST-fetched lists.
      pendingEntities: {
        opportunities: overlay.opportunities,
        requests: overlay.requests,
        auditEntries: overlay.auditEntries,
        requestOverrides: overlay.requestOverrides,
      },
      loading,
      error,
      ...(merged || baseEmpty),
    };
  }, [merged, rawBundle, tenant, tenantId, loading, error, setMode, setDemoTenantId, submitOpportunityRequest, advanceDeploymentStage, promoteOpportunity, resetDemo, replayDemo, recordAuditChainVerify, overlay]);

  return <TenantModeContext.Provider value={value}>{children}</TenantModeContext.Provider>;
}
